import React, {useCallback, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {Screen} from '../../components';
import {UnassignedTasksSidebar} from './Components/unassignedTasksSidebar/UnassignedTasksSidebar';
import {
  AbsencesLoader,
  AbsenceTypesLoader,
  getDailyUnassignedTasks,
  getPeriod,
  MembersLoader,
  OptimizationsLoader,
  ScheduleDataContext,
  useScheduleDataContextProvider,
} from '../../services';
import {ScheduleCalendarType} from '../../services';
import {
  ScheduleCalendarToolbar,
  ScheduleSceneHeader,
  ScheduleLoader,
  WarnLeaveEditing,
} from './Components';
import {ScheduleCalendar} from './Components';
import {ConfirmEditPanel} from './Components/ConfirmEditPanel';
import {Layout} from '@di-nxt/components';

type Props = {
  teamId: number;
  calendarType: ScheduleCalendarType;
  selectedDate: Date;
  onChange: (date: Date, calendarType: ScheduleCalendarType) => void;
};

export const Schedule: React.FC<Props> = ({
  teamId,
  calendarType,
  selectedDate,
  onChange,
}) => {
  const [showUnassignedTasks, setShowUnassignedTasks] = useState(false);

  const handleChange = useCallback(
    (nextDate: Date, nextCalendarType: ScheduleCalendarType) => {
      onChange(nextDate, nextCalendarType);
    },
    [onChange],
  );

  const handleChangeType = useCallback(
    (nextCalendarType: ScheduleCalendarType) => {
      onChange(selectedDate, nextCalendarType);
    },
    [onChange, selectedDate],
  );

  const handleChangeDate = useCallback(
    (nextDate: Date) => {
      onChange(nextDate, calendarType);
    },
    [calendarType, onChange],
  );

  const handleShowUnassignedTasks = useCallback(() => {
    setShowUnassignedTasks(!showUnassignedTasks);
  }, [showUnassignedTasks]);

  const provider = useScheduleDataContextProvider(
    teamId,
    selectedDate,
    calendarType,
  );

  const unassignedTasks = useMemo(
    () => getDailyUnassignedTasks(selectedDate, provider),
    [selectedDate, provider],
  );

  const {fromDate, toDate} = useMemo(() => {
    const {from, to} = getPeriod(selectedDate, calendarType);
    return {
      fromDate: dayjs(from).format('YYYY-MM-DD'),
      toDate: dayjs(to).format('YYYY-MM-DD'),
    };
  }, [calendarType, selectedDate]);

  return (
    <Screen>
      <ScheduleDataContext.Provider value={provider}>
        {/** Loaders */}
        <MembersLoader teamId={teamId} />
        <ScheduleLoader teamId={teamId} />
        <AbsenceTypesLoader teamId={teamId} />
        <OptimizationsLoader teamId={teamId} onlyActive={true} />
        <AbsencesLoader teamId={teamId} fromDate={fromDate} toDate={toDate} />
        {/** Sheet with task data */}
        <ScheduleSceneHeader
          teamId={teamId}
          calendarType={calendarType}
          onCalendarTypeChange={handleChangeType}
        />
        <WarnLeaveEditing />
        <Layout horizontal height="100%" flex justify="space-between">
          <Layout vertical width="100%">
            {/** Toolbar */}
            <ScheduleCalendarToolbar
              teamId={teamId}
              selectedDate={selectedDate}
              calendarType={calendarType}
              onSelectedDateChange={handleChangeDate}
              dailyUnassignedTasks={unassignedTasks.length}
              onShowUnassignedTasks={handleShowUnassignedTasks}
              showButton={showUnassignedTasks}
            />
            {/** Schedule component */}
            <ScheduleCalendar onChange={handleChange} />
            {/** Confirm */}
            <ConfirmEditPanel />
          </Layout>
          {/** Unassigned tasks sidebar */}
          <UnassignedTasksSidebar
            show={showUnassignedTasks && calendarType === 'day'}
            unassignedTasks={unassignedTasks}
            onHideSidebar={handleShowUnassignedTasks}
            onBeginDrag={provider.beginDragTask}
            onEndDrag={provider.cancelDragTask}
            onBeginHover={provider.beginHoverTaskOverUnassigned}
            onEndHover={provider.endHoverTaskOverUnassigned}
            onDrop={provider.dropTaskOnUnassigned}
          />
        </Layout>
      </ScheduleDataContext.Provider>
    </Screen>
  );
};
